import React from 'react';
import { useParams } from 'react-router-dom';
import { useResponsive } from '@parsina/framework';

import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import usePatientRecordScreen from './usePatientRecordScreen.js';
import PatientRecordWeb from './PatientRecord.web.jsx';
import PatientRecordMobile from './PatientRecord.mobile.jsx';
import { resolveRecordTab, DEFAULT_RECORD_TAB, isRecordTabParam } from './PatientRecord.shared.js';

// The record's route wrapper: it reads `:caseId` and `:tab?` and owns nothing
// else.
//
// The tab is a URL SEGMENT because a refresh, a back button and a pasted link
// all have to restore the part of the record the student was reading. A stale
// or hand-edited segment (`/patients/x/nonsense`) is CORRECTED — quietly, with
// `replace` — rather than rendered as an empty tab: pushed, the back button
// would return the student to the broken URL, which would correct itself again,
// which is a trap they cannot leave by going back.

const PatientRecordScreen = () => {
  const { isMobile } = useResponsive();
  const { caseId, tab: tabParam } = useParams();
  const navigate = useAppNavigate();

  const tab = resolveRecordTab(tabParam);
  const screen = usePatientRecordScreen(caseId);

  React.useEffect(() => {
    if (tabParam && !isRecordTabParam(tabParam)) {
      navigate('patient', { caseId, tab: DEFAULT_RECORD_TAB, replace: true });
    }
  }, [tabParam, caseId, navigate]);

  const onTab = React.useCallback(
    (next) => navigate('patient', { caseId, tab: next }),
    [navigate, caseId],
  );

  const Surface = isMobile ? PatientRecordMobile : PatientRecordWeb;
  return <Surface screen={screen} tab={tab} onTab={onTab} caseId={caseId} />;
};

export default PatientRecordScreen;
